<template>
  <section class="ev-services-section">
    <div class="container">
      <div class="section-header">
        <div class="section-tag">
          <i :class="headerData.icon"></i>
          <span>{{ headerData.tag }}</span>
        </div>
        <h2>{{ headerData.title }}</h2>
        <p class="section-description">{{ headerData.description }}</p>
        <div class="navigation-arrows d-flex justify-content-center">
          <button class="arrow-btn prev-btn">
            <i class="fas fa-chevron-left"></i>
          </button>
          <button class="arrow-btn next-btn">
            <i class="fas fa-chevron-right"></i>
          </button>
        </div>
      </div>
    </div>
    
    <div class="ev-services-slider swiper" ref="evServicesSwiper">
      <div class="swiper-wrapper">
        <div class="swiper-slide" v-for="(service, index) in servicesData" :key="index">
          <div class="ev-service-card">
            <div class="service-image-container">
              <img :src="service.image" :alt="service.title" class="service-image" />
              <div class="electric-badge">
                <i :class="badgeData.icon"></i>
                <span>{{ badgeData.text }}</span>
              </div>
              <div class="service-overlay">
                <div class="service-content">
                  <h3 class="service-title">{{ service.title }}</h3>
                  <p class="service-description">{{ service.description }}</p>
                  <div class="service-features">
                    <span v-for="(feature, featureIndex) in service.features" :key="featureIndex" class="feature-tag">{{ feature }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { onMounted, ref } from 'vue'

export default {
  name: "FullScreenSliderComp2",
  props: {
    headerData: {
      type: Object,
      required: true,
      default: () => ({
        icon: "fas fa-bolt",
        tag: "ELECTRIC EXCELLENCE",
        title: "Electric Vehicle Services",
        description: "Revolutionary electric vehicle solutions powered by cutting-edge technology and sustainable innovation"
      })
    },
    servicesData: {
      type: Array,
      required: true,
      default: () => []
    },
    badgeData: {
      type: Object,
      required: true,
      default: () => ({
        icon: "fas fa-bolt",
        text: "EV"
      })
    }
  },
  setup() {
    const evServicesSwiper = ref(null)

    const initEVServicesSwiper = () => {
      setTimeout(() => {
        const checkSwiper = () => {
          if (window.Swiper) {
            evServicesSwiper.value = new window.Swiper('.ev-services-slider', {
              loop: true,
              autoplay: {
                delay: 4000,
                disableOnInteraction: false,
              },
              navigation: {
                nextEl: '.next-btn',
                prevEl: '.prev-btn',
              },
              breakpoints: {
                0: {
                  slidesPerView: 1,
                  spaceBetween: 20,
                },
                768: {
                  slidesPerView: 2,
                  spaceBetween: 30,
                },
                1024: {
                  slidesPerView: 3,
                  spaceBetween: 30,
                },
                1200: {
                  slidesPerView: 4,
                  spaceBetween: 30,
                }
              }
            });
          } else {
            setTimeout(checkSwiper, 100);
          }
        };
        checkSwiper();
      }, 500);
    }

    onMounted(() => {
      initEVServicesSwiper()
    })

    return {
      evServicesSwiper
    }
  }
};
</script>

<style scoped>
/* Import Google Fonts */
@import url('https://fonts.googleapis.com/css2?family=Archivo:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&family=Open+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;1,300;1,400;1,500;1,600;1,700;1,800&display=swap');

/* Import Flaticon */
@import '@/assets/scss/Flaticon.scss';

/* Electric Vehicle Services Section */
.ev-services-section {
  padding: 100px 0;
  background: linear-gradient(135deg, #0a0f1c 0%, #1a2332 50%, #0a0f1c 100%);
  color: #fff;
  position: relative;
  overflow: hidden;
}

.ev-services-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(45deg, 
    rgba(0, 255, 150, 0.05) 0%, 
    rgba(0, 255, 200, 0.03) 25%, 
    rgba(0, 255, 100, 0.05) 50%, 
    rgba(0, 255, 150, 0.03) 75%, 
    rgba(0, 255, 200, 0.05) 100%);
  pointer-events: none;
}

.section-header {
  text-align: center;
  margin-bottom: 80px;
  position: relative;
  z-index: 2;
}

.section-tag {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(0, 255, 150, 0.1);
  border: 1px solid rgba(0, 255, 150, 0.3);
  padding: 8px 16px;
  border-radius: 25px;
  margin-bottom: 20px;
  font-size: 12px;
  font-weight: 600;
  color: #00ff96;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.section-header h2 {
  font-size: 48px;
  font-weight: 700;
  color: #fff;
  margin: 0 0 15px 0;
  font-family: 'Archivo', sans-serif;
}

.section-description {
  font-size: 18px;
  color: rgba(255, 255, 255, 0.8);
  margin: 0 0 30px 0;
  line-height: 1.6;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
}

.navigation-arrows {
  display: flex;
  gap: 10px;
  justify-content: center;
}

.arrow-btn {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(0, 255, 150, 0.1);
  border: 1px solid rgba(0, 255, 150, 0.3);
  color: #00ff96;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 14px;
}

.arrow-btn:hover {
  background: rgba(0, 255, 150, 0.2);
  border-color: rgba(0, 255, 150, 0.5);
  color: #fff;
}

.ev-services-slider {
  width: 100%;
  padding: 0 20px;
  position: relative;
  z-index: 2;
}

.ev-service-card {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(0, 255, 150, 0.2);
  border-radius: 15px;
  overflow: hidden;
  transition: all 0.3s ease;
  height: 100%;
  margin: 0 10px;
}

.ev-service-card:hover {
  transform: translateY(-5px);
  border-color: rgba(0, 255, 150, 0.4);
  box-shadow: 0 15px 40px rgba(0, 255, 150, 0.1);
}

.service-image-container {
  position: relative;
  height: 200px;
  overflow: hidden;
}

.service-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.ev-service-card:hover .service-image {
  transform: scale(1.05);
}

.electric-badge {
  position: absolute;
  top: 15px;
  right: 15px;
  background: linear-gradient(135deg, #00ff96 0%, #00ff64 100%);
  color: #000;
  padding: 6px 10px;
  border-radius: 15px;
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 4px 15px rgba(0, 255, 150, 0.3);
}

.service-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, 
    rgba(0, 0, 0, 0.7) 0%, 
    rgba(0, 0, 0, 0.3) 50%, 
    rgba(0, 0, 0, 0.7) 100%);
  display: flex;
  align-items: flex-end;
  padding: 20px;
  opacity: 0;
  transition: all 0.3s ease;
}

.ev-service-card:hover .service-overlay {
  opacity: 1;
}

.service-content {
  width: 100%;
}

.service-title {
  font-size: 18px;
  font-weight: 700;
  color: #fff;
  margin: 0 0 8px 0;
  line-height: 1.3;
  font-family: 'Archivo', sans-serif;
}

.service-description {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.9);
  margin: 0 0 15px 0;
  line-height: 1.4;
}

.service-features {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.feature-tag {
  background: rgba(0, 255, 150, 0.2);
  color: #00ff96;
  padding: 3px 8px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border: 1px solid rgba(0, 255, 150, 0.3);
}

/* Responsive Design */
@media (max-width: 991px) {
  .section-header h2 {
    font-size: 36px;
  }
  
  .section-description {
    font-size: 16px;
  }
}

@media (max-width: 767px) {
  .section-header h2 {
    font-size: 28px;
  }
  
  .section-description {
    font-size: 14px;
  }
  
  .ev-services-slider {
    padding: 0 10px;
  }
  
  .ev-service-card {
    margin: 0 5px;
  }
  
  .service-image-container {
    height: 180px;
  }
  
  .service-title {
    font-size: 16px;
  }
}
</style>
